import moment from 'moment';
import { parseJSON } from './json';
import { API_ENDPOINT } from '../constants';

/**
* Data access for the external API request log (VD-725 follow-up): the list
* behind the standalone "External API Logs" page / the client's "API logs"
* tab, and the single-log detail drawer both use.
*/

export const STATUS_CLASSES = ['2xx', '3xx', '4xx', '5xx'];
export const AUTH_METHODS = ['basic', 'apiKey', 'none'];
export const LOG_SORT_FIELDS = ['createdAt', 'durationMs', 'status', 'db.queries', 'db.totalMs'];

export const DEFAULT_LOGS_ENTRIES = 50;
export const MAX_LOGS_ENTRIES = 100;

const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
});

/** statusClass helper for a concrete status code, e.g. 404 -> '4xx' */
export const statusClassOf = (status) => {
    let n = Number(status);
    if (isNaN(n)) return null;
    return `${Math.floor(n / 100)}xx`;
};

export const statusBadgeClass = (status) => {
    let cls = statusClassOf(status);

    if (cls === '2xx') return 'api-key-badge api-key-badge-active';
    if (cls === '3xx') return 'api-key-badge api-key-badge-grace';
    if (cls === '4xx') return 'api-key-badge api-key-badge-disabled';
    if (cls === '5xx') return 'api-key-badge api-key-badge-expired';

    return 'api-key-badge api-key-badge-revoked';
};

/**
* The Insights panels (and the Analytics tables) hand "open the logs view"
* callbacks a plain `{ from, to, ...otherFilters }` object - a raw ISO range,
* since each row usually carries its own window (a burst's own minute, say).
* The logs component itself stores the period as `preset`/`customFrom`/
* `customTo` the way its own date pickers edit it, so this is the one place
* that translates between the two before the filters are applied.
*/
export const toLogsFilters = ({ from, to, ...rest }) => {
    let filters = { ...rest };

    if (from && to) {
        filters.preset = 'custom';
        filters.customFrom = moment(from).format('YYYY-MM-DDTHH:mm');
        filters.customTo = moment(to).format('YYYY-MM-DDTHH:mm');
    }

    return filters;
};

const buildQuery = (filters) => {
    let qs = new URLSearchParams();
    let keys = [
        'from', 'to', 'clientId', 'apiKeyId', 'route', 'method', 'path',
        'status', 'statusClass', 'minDurationMs', 'minDbQueries', 'fingerprint', 'requestId',
        'ip', 'authMethod', 'authErrorCode', 'authenticated', 'bodyHash', 'replica',
        'sortField', 'sortType', 'page', 'entries',
    ];

    keys.forEach((key) => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
            qs.set(key, filters[key]);
        }
    });

    return qs.toString();
};

/** @returns {Promise<{items: object[], total: number, error: string|null}>} */
export const fetchExternalApiLogs = (filters) => {
    let qs = buildQuery(filters || {});

    return fetch(`${API_ENDPOINT}/data/external-api/logs${qs ? '?' + qs : ''}`, {
        method: 'GET',
        headers: authHeaders(),
    }).then(parseJSON).then(({ result, status }) => {
        if (status >= 200 && status < 300) {
            return { items: (result && result.items) || [], total: (result && result.total) || 0, error: null };
        }

        return { items: [], total: 0, error: result && result.error ? result.error : 'Failed to load logs' };
    });
};

/** @returns {Promise<{data: object|null, error: string|null}>} */
export const fetchExternalApiLog = (id) => {
    return fetch(`${API_ENDPOINT}/data/external-api/logs/${id}`, {
        method: 'GET',
        headers: authHeaders(),
    }).then(parseJSON).then(({ result, status }) => {
        if (status >= 200 && status < 300) {
            return { data: result, error: null };
        }

        return { data: null, error: result && result.error ? result.error : 'Failed to load the log' };
    });
};
